/**
 * Lane CAMERA-PITCH: the horizon at a low pitch (render/pitchView.ts). Below `SLICE_PITCH` only a slice of the
 * world round the player is drawn; its far edge is a straight row on screen. Over the ground layer (after the turned
 * ground ends, before the scenery and figures, so tall things stand up against it) this draws:
 * - the sky above that row: a painted strip in dithered steps with two far ridges and mist at their feet, in the
 *   colours of the floating-diorama sky (skyDiorama.ts), repeating round the circle and sliding with the camera's yaw;
 * - a dithered haze over the farthest ground rows, so the slice's edge reads as distance, not a cut;
 * - below the ground drawn (only at the very flattest pitches), the ground right in front of the camera, in shade.
 * The time-of-day grade goes over it with the rest of the world picture.
 */
import { pitchBand, pitchGroundRows } from '../pitchView';
import { canvas, ctx2d } from '../pixel';
import { registerRenderPass } from './registry';

/** The sky strip: one period round the circle is `SKY_W` pixels; it repeats twice a full turn. */
const SKY_W = 1024;
const SKY_H = 200;
const HAZE_H = 14;

type RGB = [number, number, number];
const BAY = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map((v) => (v + 0.5) / 16);
const bay = (x: number, y: number) => BAY[(y & 3) * 4 + (x & 3)]!;
const mix = (a: RGB, b: RGB, t: number): RGB => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];
/** Top of the sky to the horizon (skyDiorama.ts's stops above its horizon). */
const STOPS: [number, RGB][] = [
  [0, [62, 74, 128]],
  [0.5, [118, 112, 166]],
  [0.85, [214, 170, 168]],
  [1, [236, 196, 170]],
];
const HORIZON: RGB = [236, 196, 170];
const TOP: RGB = STOPS[0]![1];
const hex = (c: RGB) => `rgb(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])})`;

function ramp(t: number): RGB {
  for (let i = 1; i < STOPS.length; i++) {
    const [t1, c1] = STOPS[i]!;
    if (t <= t1) {
      const [t0, c0] = STOPS[i - 1]!;
      return mix(c0, c1, (t - t0) / (t1 - t0));
    }
  }
  return HORIZON;
}

/** A ridge line that repeats every `SKY_W` pixels: sums of whole-period sines. */
function ridge(x: number, seed: number, amp: number): number {
  const a = (x / SKY_W) * Math.PI * 2;
  let v = 0;
  let w = 0;
  for (const [f, k] of [
    [3, 1],
    [7, 0.55],
    [13, 0.3],
    [29, 0.14],
  ] as const) {
    v += k * Math.sin(a * f + seed * (f + 1.7));
    w += k;
  }
  return amp * (0.5 + 0.5 * (v / w));
}

let sky: HTMLCanvasElement | null = null;
function paintSky(): HTMLCanvasElement {
  if (sky) return sky;
  const c = canvas(SKY_W, SKY_H);
  const g = ctx2d(c);
  const id = g.createImageData(SKY_W, SKY_H);
  const d = id.data;
  const N = 12;
  const ridges = [
    { amp: 46, base: 4, col: [150, 140, 182] as RGB, rim: [204, 178, 196] as RGB, seed: 1 },
    { amp: 26, base: 0, col: [112, 104, 156] as RGB, rim: [164, 142, 180] as RGB, seed: 2 },
  ];
  const tops = ridges.map((r) => {
    const a = new Int16Array(SKY_W);
    for (let x = 0; x < SKY_W; x++) a[x] = SKY_H - 1 - r.base - Math.round(ridge(x, r.seed, r.amp));
    return a;
  });
  for (let y = 0; y < SKY_H; y++)
    for (let x = 0; x < SKY_W; x++) {
      const L = (y / (SKY_H - 1)) * N;
      let col = ramp((Math.floor(L) + (L % 1 > bay(x, y) ? 1 : 0)) / N);
      for (let k = 0; k < ridges.length; k++) {
        const r = ridges[k]!;
        const top = tops[k]![x]!;
        if (y >= top) col = y < top + 1 ? r.rim : r.col;
        else if (y > top - 5 && (y - (top - 5)) / 5 > bay(x, y)) col = mix(col, [232, 214, 226], 0.3);
      }
      // Mist pooled along the horizon over the near ridge's foot.
      const m = (y - (SKY_H - 10)) / 10;
      if (m > 0 && m > bay(x + 1, y)) col = mix(col, HORIZON, 0.55);
      const i = (y * SKY_W + x) * 4;
      d[i] = col[0];
      d[i + 1] = col[1];
      d[i + 2] = col[2];
      d[i + 3] = 255;
    }
  g.putImageData(id, 0, 0);
  return (sky = c);
}

/** The haze over the farthest ground rows: the horizon's colour, thinning in dithered steps. */
let haze: HTMLCanvasElement | null = null;
function paintHaze(): HTMLCanvasElement {
  if (haze) return haze;
  const c = canvas(SKY_W, HAZE_H);
  const g = ctx2d(c);
  const id = g.createImageData(SKY_W, HAZE_H);
  const d = id.data;
  for (let y = 0; y < HAZE_H; y++)
    for (let x = 0; x < SKY_W; x++) {
      const t = 1 - (Math.floor((y / HAZE_H) * 4) + 0.5) / 4; // four steps
      if (t * 0.85 <= bay(x, y)) continue;
      const i = (y * SKY_W + x) * 4;
      d[i] = HORIZON[0];
      d[i + 1] = HORIZON[1];
      d[i + 2] = HORIZON[2];
      d[i + 3] = 255;
    }
  g.putImageData(id, 0, 0);
  return (haze = c);
}

/** The ground right in front of the camera (the flattest pitches only): field ground in shade, darker downward. */
let near: HTMLCanvasElement | null = null;
function paintNear(): HTMLCanvasElement {
  if (near) return near;
  const H = 64;
  const c = canvas(SKY_W, H);
  const g = ctx2d(c);
  const id = g.createImageData(SKY_W, H);
  const d = id.data;
  const a: RGB = [104, 116, 70];
  const b: RGB = [70, 78, 52];
  for (let y = 0; y < H; y++)
    for (let x = 0; x < SKY_W; x++) {
      const L = (y / (H - 1)) * 3;
      const col = mix(a, b, (Math.floor(L) + (L % 1 > bay(x, y) ? 1 : 0)) / 3);
      const i = (y * SKY_W + x) * 4;
      d[i] = col[0];
      d[i + 1] = col[1];
      d[i + 2] = col[2];
      d[i + 3] = 255;
    }
  g.putImageData(id, 0, 0);
  return (near = c);
}

/** Draws a strip that repeats every `SKY_W` pixels across the picture, starting `x0` (mod `SKY_W`). */
function tile(g: CanvasRenderingContext2D, img: HTMLCanvasElement, x0: number, y: number, W: number, h = img.height) {
  for (let x = (((x0 % SKY_W) + SKY_W) % SKY_W) - SKY_W; x < W; x += SKY_W)
    g.drawImage(img, 0, 0, SKY_W, h, x, y, SKY_W, h);
}

registerRenderPass({
  id: 'pitchHorizon',
  layer: 'ground',
  order: 902,
  draw(f) {
    if (!f.outdoors) return;
    const p = f.world.player;
    const band = pitchBand(f.iso, f.iso.depth(p.x, p.y), f.player[1], f.oy, f.H);
    if (!band) return;
    const g = f.g;
    // Two periods of the strip a full turn round: it slides with the yaw like a far backdrop.
    const x0 = Math.round(-(f.iso.angle / 4) * SKY_W * 2);
    const hy = Math.round(band.horizon);
    if (hy > 0) {
      if (hy > SKY_H) {
        g.fillStyle = hex(TOP);
        g.fillRect(0, 0, f.W, hy - SKY_H);
      }
      tile(g, paintSky(), x0, hy - SKY_H, f.W);
      tile(g, paintHaze(), x0, hy, f.W);
    }
    const [, bottom] = pitchGroundRows(band, f.player[1], f.oy, f.H);
    if (bottom < f.H) {
      const by = Math.floor(bottom);
      const nh = Math.min(64, f.H - by);
      tile(g, paintNear(), x0, by, f.W, nh);
      if (by + nh < f.H) {
        g.fillStyle = 'rgb(70,78,52)';
        g.fillRect(0, by + nh, f.W, f.H - by - nh);
      }
    }
  },
});
